/**
 * Subscription Management Page - Trade Loop v2.0
 *
 * Displays current subscription details and management options:
 * - Current plan and status
 * - Billing information
 * - Upgrade/downgrade options
 * - Cancel subscription
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckoutButton } from '@/components/subscription/CheckoutButton';
import { InvoiceList } from '@/components/subscription/InvoiceList';
import { PendingPlanChangeCard } from '@/components/subscription/PendingPlanChangeCard';
import { CancelSubscriptionModal } from '@/components/subscription/CancelSubscriptionModal';
import { usePricing } from '@/hooks/usePricing';
import { isPlanPurchasable } from '@/config/subscription-plan-limits';
import { useToast } from '@/contexts/ToastContext';
import { timezoneHeaders } from '@/utils/timezone';
import {
  CreditCard,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Crown,
  BarChart3,
  Users,
  Send,
  ArrowRightLeft,
} from 'lucide-react';

export default function SubscriptionPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess, showInfo } = useToast();
  const { getPlanPrice } = usePricing();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [seatInfo, setSeatInfo] = useState<any>(null);
  const [pendingPlanChange, setPendingPlanChange] = useState<any>(null);
  // Cancelling ends a paid plan and is not something to do on a mis-tap, so it is confirmed.
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Card 19 task B1. Null unless this account is comped. Kept separate from `currentPlan`
  // deliberately: the plan the user gets and the reason they get it are different facts, and
  // conflating them is how a comped account becomes indistinguishable from a paying one.
  const [planGrant, setPlanGrant] = useState<any>(null);

  // Check if user is an organization member - redirect to organization page
  const isOrganizationMember = user?.organizationMembership?.role === 'member';

  // Redirect organization members to organization page
  useEffect(() => {
    if (isOrganizationMember) {
      navigate('/organization', { replace: true });
    }
  }, [isOrganizationMember, navigate]);

  // Fetch real data from backend
  const fetchData = async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoadingData(true);

      // Fetch dashboard stats (includes usage data)
      const dashboardResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/dashboard`, {
        credentials: 'include',
        // Raw fetch, so the header lib/api.ts adds automatically has to be added by hand — the
        // usage counts are windowed on the caller's calendar month (see utils/timezone.ts).
        headers: timezoneHeaders(),
      });

      let dashboardStats = null;
      if (dashboardResponse.ok) {
        dashboardStats = await dashboardResponse.json();
        setDashboardData(dashboardStats);
      }

      // Fetch subscription details
      const subscriptionResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/current`, {
        credentials: 'include',
      });

      let subscriptionInfo = null;
      if (subscriptionResponse.ok) {
        const response = await subscriptionResponse.json();
        subscriptionInfo = response.data?.subscription || null;
        setSubscriptionData(subscriptionInfo);

        // Check for pending plan changes
        if (response.data?.pendingPlanChange) {
          setPendingPlanChange(response.data.pendingPlanChange);
        } else {
          setPendingPlanChange(null);
        }
      }

      // Fetch any active comp grant so the page can say "comped" rather than silently showing
      // a plan the user is not paying for.
      try {
        const grantResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/plan-grants/me`, {
          credentials: 'include',
        });
        if (grantResponse.ok) {
          const grantJson = await grantResponse.json();
          setPlanGrant(grantJson.data ?? null);
        }
      } catch (error) {
        // Non-fatal: without this the page just shows the plan without the comp note.
        console.log('Failed to fetch plan grant:', error);
      }

      // Fetch seat information if user is on Grow plan (only org owners can be on Grow)
      // Check plan from: subscription data (most reliable) > dashboard > user profile
      const plan = subscriptionInfo?.plan?.toLowerCase()
        || dashboardStats?.subscriptionPlan?.toLowerCase()
        || user?.subscriptionPlan?.toLowerCase()
        || 'starter';

      console.log('Current plan for seat check:', plan);

      if (plan === 'grow') {
        try {
          const seatsResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/organization/seats`, {
            credentials: 'include',
          });

          if (seatsResponse.ok) {
            const seatsData = await seatsResponse.json();
            setSeatInfo(seatsData.data);
            console.log('Seat info loaded:', seatsData.data);
          } else {
            console.log('Failed to fetch seats, status:', seatsResponse.status);
          }
        } catch (error) {
          console.log('Failed to fetch seats (may not be org owner):', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      showError('Failed to Load Data', 'Unable to fetch subscription details');
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isAuthenticated, user]);

  // Use real data from API or fallback to user profile
  const currentPlan = dashboardData?.subscriptionPlan?.toLowerCase() || user?.subscriptionPlan?.toLowerCase() || 'starter';
  const subscriptionStatus = dashboardData?.subscriptionStatus || subscriptionData?.status || 'active';
  const nextBillingDate = subscriptionData?.currentPeriodEnd ? new Date(subscriptionData.currentPeriodEnd) : null;

  // Get billing amount from Stripe pricing.
  // A comped account is billed nothing at all — there is no Stripe subscription behind it — so
  // showing the plan's list price here would be plainly wrong, not merely unhelpful.
  const isComped = Boolean(planGrant);
  const basePlanAmount = currentPlan === 'starter' || isComped
    ? 0
    : getPlanPrice(currentPlan as 'pro' | 'grow');

  // Add additional seat costs for Grow plan
  const additionalSeatCost = isComped ? 0 : (seatInfo?.additional || 0) * (seatInfo?.costPerAdditional || 5);
  const billingAmount = basePlanAmount + additionalSeatCost;

  const handleCancelSubscription = async () => {
    try {
      setIsLoading(true);
      showInfo('Canceling subscription...', 'Please wait');

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Failed to cancel subscription';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          // Response body is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      showSuccess('Subscription Canceled', 'Your subscription will remain active until the end of the billing period');

      // Refresh data to show updated status
      const dashboardResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/dashboard`, {
        credentials: 'include',
        // Raw fetch, so the header lib/api.ts adds automatically has to be added by hand — the
        // usage counts are windowed on the caller's calendar month (see utils/timezone.ts).
        headers: timezoneHeaders(),
      });
      if (dashboardResponse.ok) {
        const dashboardStats = await dashboardResponse.json();
        setDashboardData(dashboardStats);
      }

      // Also refresh subscription details
      const subscriptionResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/current`, {
        credentials: 'include',
      });
      if (subscriptionResponse.ok) {
        const response = await subscriptionResponse.json();
        setSubscriptionData(response.data?.subscription || null);
      }
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      showError('Failed to Cancel', error.message || 'Unable to cancel subscription at this time');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSeat = async () => {
    try {
      setIsLoading(true);

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/organization/seats/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ quantity: 1 })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel seat');
      }

      const result = await response.json();

      showSuccess('Seat Removed', result.message || 'Seat removed. You will not be charged in your next billing cycle.');

      // Refresh seat info
      const seatsResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/organization/seats`, {
        credentials: 'include',
      });

      if (seatsResponse.ok) {
        const seatsData = await seatsResponse.json();
        setSeatInfo(seatsData.data);
      }

    } catch (error: any) {
      console.error('Cancel seat error:', error);
      showError('Failed to Cancel Seat', error.message || 'Unable to cancel seat at this time');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsLoading(true);
      showInfo('Reactivating subscription...', 'Please wait');

      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = 'Failed to reactivate subscription';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch (e) {
          // Response body is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      showSuccess('Subscription Reactivated', 'Your subscription has been successfully reactivated');

      // Refresh data to show updated status
      const dashboardResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/dashboard`, {
        credentials: 'include',
        // Raw fetch, so the header lib/api.ts adds automatically has to be added by hand — the
        // usage counts are windowed on the caller's calendar month (see utils/timezone.ts).
        headers: timezoneHeaders(),
      });
      if (dashboardResponse.ok) {
        const dashboardStats = await dashboardResponse.json();
        setDashboardData(dashboardStats);
      }

      // Also refresh subscription details
      const subscriptionResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/subscriptions/current`, {
        credentials: 'include',
      });
      if (subscriptionResponse.ok) {
        const response = await subscriptionResponse.json();
        setSubscriptionData(response.data?.subscription || null);
      }
    } catch (error: any) {
      console.error('Reactivate subscription error:', error);
      showError('Failed to Reactivate', error.message || 'Unable to reactivate subscription at this time');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = () => {
    switch (subscriptionStatus) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Active
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Canceled
          </span>
        );
      case 'past_due':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
            <AlertCircle className="w-4 h-4" />
            Past Due
          </span>
        );
      default:
        return null;
    }
  };

  const getPlanIcon = () => {
    if (currentPlan === 'grow') {
      return <Crown className="w-6 h-6 text-purple-600" />;
    } else if (currentPlan === 'pro') {
      return <TrendingUp className="w-6 h-6 text-blue-600" />;
    }
    return <Users className="w-6 h-6 text-gray-600" />;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (!isAuthenticated) {
    return (
      <AppLayout activeTab="profile" title="Subscription" backLink="/settings">
        <div className="max-w-4xl mx-auto p-4">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
              <p className="text-gray-600">Please log in to view your subscription details.</p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeTab="profile" title="Subscription" backLink="/settings">
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Current Plan Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                {getPlanIcon()}
                Current Plan
              </span>
              {getStatusBadge()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Plan Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Plan</p>
                <p className="text-xl font-bold text-gray-900 capitalize">{currentPlan}</p>
                {planGrant && (
                  <p className="text-xs text-green-700 mt-1">
                    Complimentary &mdash;{' '}
                    {planGrant.expiresAt
                      ? `free until ${new Date(planGrant.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`
                      : 'no end date'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Billing Amount</p>
                <p className="text-xl font-bold text-gray-900">
                  {billingAmount === 0 ? 'Free' : `$${billingAmount}/month`}
                </p>
                {seatInfo && seatInfo.additional > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    ${basePlanAmount} plan + ${additionalSeatCost} ({seatInfo.additional} seat{seatInfo.additional > 1 ? 's' : ''})
                  </p>
                )}
              </div>
              {billingAmount > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {subscriptionData?.cancelAtPeriodEnd ? 'Subscription Active Until' : 'Next Billing Date'}
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {nextBillingDate ? formatDate(nextBillingDate) : 'Loading...'}
                  </p>
                </div>
              )}
            </div>

            {/* Cancellation Notice */}
            {subscriptionData?.cancelAtPeriodEnd && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">Subscription Canceled</p>
                  <p className="text-sm text-amber-700">
                    Your subscription will remain active until {nextBillingDate ? formatDate(nextBillingDate) : 'the end of the billing period'}.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            {/* Stacked on phones: two full-width buttons side by side overflow a narrow
                viewport, and "Cancel Subscription" is long enough to wrap mid-word. */}
            <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/plans')}
                className="flex-1"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Change Subscription
              </Button>

              {/* Cancel/Reactivate Button - 50/50 split with Change Subscription */}
              {billingAmount > 0 && (
                <>
                  {subscriptionData?.cancelAtPeriodEnd ? (
                    <Button
                      onClick={handleReactivateSubscription}
                      disabled={isLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold shadow-md"
                    >
                      {isLoading ? 'Reactivating...' : 'Reactivate Subscription'}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowCancelModal(true)}
                      disabled={isLoading}
                      variant="outline"
                      className="flex-1 border-2 border-red-500 text-red-600 hover:bg-red-50 font-semibold shadow-sm"
                    >
                      {isLoading ? 'Canceling...' : 'Cancel Subscription'}
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pending Plan Change Card */}
        {pendingPlanChange && (
          <PendingPlanChangeCard
            planChangeId={pendingPlanChange.id}
            currentPlan={currentPlan}
            newPlan={pendingPlanChange.new_plan}
            effectiveDate={new Date(pendingPlanChange.effective_date)}
            onCancel={() => {
              // Refresh data after canceling
              setPendingPlanChange(null);
              fetchData();
            }}
          />
        )}

        {/* Cancellation confirmation. The component already existed and was wired to nothing, so
            Cancel Subscription fired the moment it was tapped — on a phone, next to Change
            Subscription, with no way back. */}
        <CancelSubscriptionModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelSubscription}
          currentPlan={currentPlan}
          periodEndDate={
            subscriptionData?.currentPeriodEnd
              ? new Date(subscriptionData.currentPeriodEnd)
              : undefined
          }
          isLoading={isLoading}
        />

        {/* Upgrade Options — Starter users are upsold to Pro; Pro users are only upsold to Grow
            once Grow can actually be bought (Card 24). Keyed on purchasability, not visibility:
            Grow IS visible on /plans as "Coming Soon", and an upsell card whose only button
            cannot be honoured is exactly the dead end this card exists to remove. */}
        {(currentPlan === 'starter' || (currentPlan === 'pro' && isPlanPurchasable('grow'))) && (
          <Card className="border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {currentPlan === 'starter' ? 'Unlock More Features' : 'Upgrade to Unlimited'}
                  </h3>
                  <p className="text-gray-700 mb-4">
                    {currentPlan === 'starter'
                      ? 'Upgrade to Pro for professional profile, verified badge, unlimited trades, and priority support.'
                      : 'Upgrade to Grow for team collaboration, advanced features, and custom integrations.'}
                  </p>
                  <div className="flex gap-3">
                    {currentPlan === 'starter' && (
                      <CheckoutButton
                        plan="pro"
                        buttonText={`Upgrade to Pro - $${getPlanPrice('pro')}/month`}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
                      />
                    )}
                    {isPlanPurchasable('grow') && currentPlan !== 'grow' && (
                      <CheckoutButton
                        plan="grow"
                        buttonText={`Upgrade to Grow - $${getPlanPrice('grow')}/month`}
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                      />
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing History & Invoices */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Billing History</h3>
                <p className="text-sm text-gray-500">View and download your past invoices and payment records</p>
              </div>
            </div>

            <InvoiceList limit={10} showPagination={true} />
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
